// Fiche produit SNK (sections/snk-product-main.liquid).
// - <snk-size-select> : menu déroulant des tailles qui pilote le <select> natif lu par Dawn (variant-selects) ; après
//   le changement, Dawn redemande la section et remplace tout variant-selects, ce qui re-crée l'élément avec les états
//   à jour.
// - <snk-product-buy> : bouton « Ajouter au panier · prix » repris du re-rendu de Dawn (événement variant-change),
//   fenêtres du certificat et du guide des tailles (page Shopify chargée dans la fenêtre, lien normal sans JS).
if (!customElements.get('snk-size-select')) {
  customElements.define(
    'snk-size-select',
    class SnkSizeSelect extends HTMLElement {
      connectedCallback() {
        this.native = this.querySelector('[data-snk-size-native]');
        this.toggle = this.querySelector('[data-snk-size-toggle]');
        this.menu = this.querySelector('[data-snk-size-menu]');
        this.items = [...this.querySelectorAll('[data-snk-size-value]')];
        this.toggle.addEventListener('click', () => (this.classList.contains('is-open') ? this.close() : this.open()));
        this.items.forEach((item) => item.addEventListener('click', () => this.choose(item.dataset.snkSizeValue, item.dataset.snkSizeAlt)));
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.classList.contains('is-open')) {
            this.close();
            this.toggle.focus();
          }
          if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && this.classList.contains('is-open')) {
            event.preventDefault();
            const enabled = this.items.filter((item) => !item.disabled);
            const index = enabled.indexOf(document.activeElement);
            const next = enabled[(index + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length];
            next?.focus();
          }
        });
        this.onOutside = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        // Re-rendu par Dawn après un changement d'une autre option (Box, Condition) : la taille n'a toujours pas été
        // choisie par le visiteur, on garde « Choisis ta taille » même si la variante est dans l'URL
        // (Dawn re-rend ce menu avant de publier variantChange : un choix fait dans le menu compte déjà, sizeTouched)
        const buy = this.closest('snk-product-buy');
        if (buy?.dataset.sizeChosen === 'false' && buy.dataset.sizeTouched !== 'true') this.showUnchosen();
      }

      showUnchosen() {
        this.dataset.chosen = 'false';
        this.classList.add('is-unchosen');
        const value = this.toggle.querySelector('.snk-size__value');
        if (value) {
          value.textContent = this.dataset.placeholder;
          value.classList.add('snk-size__value--placeholder');
        }
        this.toggle.querySelector('.snk-size__state')?.remove();
        this.items.forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-selected', 'false');
        });
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onOutside);
      }

      // Ouverture et fermeture animées : le panneau, dans le flux, se déroule (hauteur 0 → hauteur réelle) et pousse la
      // suite de la page ; une fois ouvert, hauteur libre (auto). Fermeture : hauteur réelle → 0, puis hidden
      open() {
        clearTimeout(this.hideTimer);
        this.menu.hidden = false;
        this.menu.style.height = '0px';
        const full = this.menu.scrollHeight;
        requestAnimationFrame(() => {
          this.menu.classList.add('is-shown');
          this.menu.style.height = `${full}px`;
        });
        const done = (event) => {
          if (event.propertyName !== 'height' || !this.classList.contains('is-open')) return;
          this.menu.style.height = 'auto';
          this.menu.removeEventListener('transitionend', done);
        };
        this.menu.addEventListener('transitionend', done);
        this.toggle.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        document.addEventListener('pointerdown', this.onOutside);
        // Sans défilement : le panneau se déroule sous le champ, la page ne doit pas sauter vers la taille choisie
        (this.items.find((item) => item.classList.contains('is-selected') && !item.disabled) ?? this.items.find((item) => !item.disabled))?.focus({ preventScroll: true });
      }

      close() {
        if (this.menu.hidden) return;
        this.menu.style.height = `${this.menu.scrollHeight}px`;
        this.menu.offsetHeight; // fixe la hauteur de départ avant de la ramener à 0
        this.menu.style.height = '0px';
        this.menu.classList.remove('is-shown');
        const ms = Math.max(...getComputedStyle(this.menu).transitionDuration.split(',').map((d) => parseFloat(d) * 1000 || 0));
        clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => (this.menu.hidden = true), ms + 20);
        this.toggle.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        document.removeEventListener('pointerdown', this.onOutside);
      }

      // alt : « Box=Full Box|Condition=Used », les autres options à régler pour que cette taille soit disponible
      choose(value, alt = '') {
        this.close();
        const scope = this.closest('variant-selects') ?? this.parentElement;
        let changed = false;
        for (const pair of alt.split('|').filter(Boolean)) {
          const [name, optionValue] = pair.split('=');
          const input = scope.querySelector(`fieldset[data-snk-option="${CSS.escape(name)}"] input[value="${CSS.escape(optionValue)}"]`);
          if (input && !input.checked) {
            input.checked = true;
            changed = true;
          }
        }
        // Taille pas encore choisie : même valeur que la variante par défaut, on la confirme quand même (Dawn re-rend)
        if (this.native.value === value && !changed && this.dataset.chosen !== 'false') return;
        // Seul ce menu vaut choix de taille (snk-product-buy le relit au re-rendu de Dawn)
        const buy = this.closest('snk-product-buy');
        if (buy) buy.dataset.sizeTouched = 'true';
        this.native.value = value;
        // Dawn (variant-selects) écoute ce change, déplace l'attribut selected et redemande la section
        this.native.dispatchEvent(new Event('change', { bubbles: true }));
        this.toggle.focus();
      }
    }
  );
}

if (!customElements.get('snk-product-buy')) {
  customElements.define(
    'snk-product-buy',
    class SnkProductBuy extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.querySelectorAll('[data-snk-dialog-open]').forEach((button) =>
          button.addEventListener('click', () => this.openDialog(button.dataset.snkDialogOpen))
        );
        this.querySelectorAll('[data-snk-dialog-close]').forEach((button) =>
          button.addEventListener('click', () => button.closest('dialog').close())
        );
        this.querySelectorAll('dialog').forEach((dialog) =>
          dialog.addEventListener('click', (event) => {
            if (event.target === dialog) dialog.close();
          })
        );
        this.setupRatingLink();
        // Lien du guide : dans variant-selects (collé au sélecteur de taille), que Dawn remplace à chaque changement de
        // variante ; écouté ici, au niveau de la colonne d'achat, il marche aussi après le remplacement
        this.addEventListener('click', (event) => {
          const guide = event.target.closest('[data-snk-size-guide]');
          if (!guide) return;
          event.preventDefault();
          this.openGuide(guide.href);
        });

        // Drapeau choisi (snk-flag-picker.is-on) : la ligne d'expédition gagne « · drapeau : +24 h » (pose avant envoi)
        const ship = this.querySelector('.snk-product__ship');
        const picker = this.querySelector('snk-flag-picker');
        if (ship && picker && ship.dataset.snkShipFlag) {
          const sync = () => {
            const on = picker.classList.contains('is-on');
            let extra = ship.querySelector('.snk-product__ship-flag');
            if (on && !extra) {
              extra = document.createElement('span');
              extra.className = 'snk-product__ship-flag';
              extra.textContent = ` · ${ship.dataset.snkShipFlag}`;
              ship.appendChild(extra);
            } else if (!on && extra) {
              extra.remove();
            }
          };
          new MutationObserver(sync).observe(picker, { attributes: true, attributeFilter: ['class'] });
          sync();
        }

        // Taille pas encore choisie : « Ajouter au panier » (et la barre collante, qui envoie le même formulaire)
        // ouvre le menu des tailles au lieu d'ajouter la 1re taille disponible sans que le visiteur l'ait vue
        this.form = document.getElementById(`product-form-${this.sectionId}`);
        this.addEventListener(
          'submit',
          (event) => {
            if (event.target !== this.form) return;
            const button = this.submitButton();
            if (this.dataset.sizeChosen !== 'false') return;
            event.preventDefault();
            event.stopPropagation();
            this.askSize();
          },
          true
        );

        // Chaussures : le bouton suit le panier (paire ajoutée par le bouton, par le drapeau, retirée du tiroir…)
        if (this.dataset.shoe === 'true' && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.syncCart());
        }

        // Le bouton de Dawn est remis à « Ajouter au panier » à chaque variante : on reprend le texte du re-rendu
        // (avec le prix de la variante, ou « Épuisée »)
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.sectionId || !data.html) return;
            // Taille choisie seulement si le changement vient du menu des tailles (pas d'une pastille Box ou Condition)
            if (this.dataset.sizeTouched === 'true') {
              this.dataset.sizeChosen = 'true';
              this.querySelector('.product-form__error-message-wrapper')?.setAttribute('hidden', '');
            }
            const nextButton = data.html.getElementById(`ProductSubmitButton-${this.sectionId}`);
            const next = nextButton?.querySelector('span');
            const current = this.querySelector(`#ProductSubmitButton-${this.sectionId} span`);
            if (next && current) current.textContent = next.textContent.trim();
            const button = this.submitButton();
            if (nextButton && button) {
              button.dataset.addText = nextButton.dataset.addText || '';
            }
            // Le tiroir drapeau reprend le bouton (paire + drapeau)
            this.dispatchEvent(new CustomEvent('snk:pair-change'));
            // Taille contrôlée sur le certificat
            const nextSize = data.html.querySelector('.snk-cert__meta');
            const currentSize = this.querySelector('.snk-cert__meta');
            if (nextSize && currentSize) currentSize.innerHTML = nextSize.innerHTML;
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.unsubscribeCart?.();
      }

      // Note Trustpilot → section avis de la page (#avis) : lien seulement si la section est là ; défilement doux
      // (saut direct si prefers-reduced-motion), arrêt sous l'en-tête collant (scroll-margin-top de #avis), puis focus
      // sur le titre de la section pour le clavier et le lecteur d'écran
      setupRatingLink() {
        const rating = this.querySelector('[data-snk-rating-link]');
        const target = document.getElementById('avis');
        if (!rating || !target) return;
        const link = document.createElement('a');
        link.href = '#avis';
        link.className = `${rating.className} snk-rating--link`;
        link.setAttribute('aria-label', rating.dataset.snkRatingLink);
        link.append(...rating.childNodes);
        rating.replaceWith(link);
        link.addEventListener('click', (event) => {
          event.preventDefault();
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
          history.replaceState(null, '', '#avis');
          const title = target.querySelector('[data-snk-reviews-title]') || target;
          const focus = () => title.focus({ preventScroll: true });
          if (reduce || !('onscrollend' in window)) setTimeout(focus, reduce ? 0 : 700);
          else window.addEventListener('scrollend', focus, { once: true });
        });
      }

      submitButton() {
        return this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
      }

      syncCart() {
        const button = this.submitButton();
        if (!button || button.disabled || this.dataset.sizeChosen === 'false') return;
        // Toujours « Ajouter au panier », même si la pointure y est déjà (LG, 2 nov. 2026)
        const span = button.querySelector('span');
        if (span) span.textContent = button.dataset.addText;
        this.dispatchEvent(new CustomEvent('snk:cart-state'));
      }

      askSize() {
        const sizes = this.querySelector('snk-size-select');
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const message = wrapper?.querySelector('.product-form__error-message');
        if (wrapper && message) {
          message.textContent = this.dataset.chooseSize;
          wrapper.removeAttribute('hidden');
        }
        sizes?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        sizes?.open();
      }

      openDialog(name) {
        this.querySelector(`[data-snk-dialog="${name}"]`)?.showModal();
      }

      // Guide des tailles interactif (snippets/snk-size-guide.liquid) dans la fenêtre ; sans fenêtre, on suit le lien vers la page
      openGuide(url) {
        const dialog = this.querySelector('[data-snk-dialog="guide"]');
        if (!dialog) return (location.href = url);
        try {
          dialog.querySelector('snk-size-guide')?.reset?.();
        } catch (error) {
          if (window.console) console.warn('snk-size-guide : remise à zéro impossible', error);
        }
        if (!dialog.open) dialog.showModal();
      }
    }
  );
}

// Option en double masquée (.snk-product__mirror, ex. « Taille » qui répète les couleurs chez Units) : elle suit la
// valeur choisie dans l'option visible, avant que variant-selects (Dawn) lise les options cochées (phase de capture).
document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || input.closest('.snk-product__mirror')) return;
    const scope = input.closest('variant-selects') || document;
    scope.querySelectorAll('.snk-product__mirror input[type="radio"]').forEach((mirror) => {
      if (mirror.value === input.value) mirror.checked = true;
    });
  },
  true
);
